import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useTenant } from '@/hooks/useTenant';

interface CategoryChip {
  key: string;
  label: string;
  icon: string;
  href: string;
  count: number;
}

interface ProductRow {
  category_id: number | null;
  product_categories: { id: number; name: string } | { id: number; name: string }[] | null;
}

interface ServiceRow { nom: string | null }

interface HebergementCategoryRow { id: number; nom: string; slug: string; icon: string | null; idcommerce: number | null }

/**
 * Balise {{categories}} — affiche les catégories du commerce sous forme de
 * filtres cliquables : produits, services et hébergements.
 * Aucune donnée inventée : un groupe vide n'est simplement pas affiché.
 */
export default function CategoriesTag() {
  const { tenant, isTenant } = useTenant();
  const merchantId = tenant?.id ?? null;

  const [productCats, setProductCats] = useState<CategoryChip[]>([]);
  const [serviceCats, setServiceCats] = useState<CategoryChip[]>([]);
  const [stayCats, setStayCats] = useState<CategoryChip[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isTenant || !merchantId) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [prodRes, servRes, stayRes] = await Promise.all([
          supabase
            .from('product_items')
            .select('category_id, product_categories(id, name)')
            .eq('idcommerce', merchantId)
            .eq('status', 'active'),
          supabase
            .from('categorieservices')
            .select('nom')
            .eq('idcommerce', merchantId),
          supabase
            .from('typehebergement')
            .select('id, nom, slug, icon, idcommerce')
            .order('sort_order', { ascending: true }),
        ]);
        if (cancelled) return;

        // Produits : on regroupe par catégorie réelle du catalogue.
        const grouped = new Map<number, { label: string; count: number }>();
        for (const row of (prodRes.data || []) as ProductRow[]) {
          const cat = Array.isArray(row.product_categories) ? row.product_categories[0] : row.product_categories;
          if (!cat) continue;
          const prev = grouped.get(cat.id);
          grouped.set(cat.id, { label: cat.name, count: (prev?.count || 0) + 1 });
        }
        setProductCats(
          Array.from(grouped.entries()).map(([id, v]) => ({
            key: `p-${id}`,
            label: v.label,
            icon: 'ri-price-tag-3-line',
            href: '/products',
            count: v.count,
          })),
        );

        // Services : catégories déclarées par le marchand.
        const servNames = Array.from(new Set(
          ((servRes.data || []) as ServiceRow[]).map((s) => (s.nom || '').trim()).filter(Boolean),
        ));
        setServiceCats(servNames.map((name, i) => ({
          key: `s-${i}-${name}`,
          label: name,
          icon: 'ri-service-line',
          href: '/services',
          count: 0,
        })));

        // Hébergements : types / catégories publiés.
        setStayCats(
          ((stayRes.data || []) as HebergementCategoryRow[])
            .filter((c) => c.idcommerce == null || Number(c.idcommerce) === Number(merchantId))
            .map((c) => ({
              key: `h-${c.id}`,
              label: c.nom,
              icon: c.icon || 'ri-hotel-line',
              href: `/hebergements?type=${encodeURIComponent(c.slug)}`,
              count: 0,
            })),
        );
      } catch {
        /* bloc silencieux */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isTenant, merchantId]);

  const groups = [
    { key: 'products', title: 'Boutique', icon: 'ri-shopping-bag-3-line', items: productCats },
    { key: 'services', title: 'Services', icon: 'ri-briefcase-line', items: serviceCats },
    { key: 'stays', title: 'Hébergements', icon: 'ri-hotel-line', items: stayCats },
  ].filter((g) => g.items.length > 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <i className="ri-loader-4-line animate-spin text-xl text-primary-500"></i>
      </div>
    );
  }

  if (groups.length === 0) return null;

  return (
    <section className="w-full px-4 md:px-6 py-10">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-secondary-50 text-secondary-700 text-xs font-medium mb-3">
            <i className="ri-apps-2-line"></i>Catégories
          </span>
          <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">Explorez par catégorie</h2>
        </div>

        <div className="space-y-6">
          {groups.map((group) => (
            <div key={group.key}>
              <h3 className="text-xs font-bold uppercase tracking-wider text-foreground-600 mb-3 flex items-center gap-2">
                <i className={group.icon}></i>{group.title}
              </h3>
              <div className="flex flex-wrap gap-2">
                {group.items.map((item) => (
                  <Link
                    key={item.key}
                    to={item.href}
                    className="flex items-center gap-2 px-3.5 py-2 rounded-full bg-background-50 border border-background-200/70 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-colors no-underline whitespace-nowrap"
                  >
                    <i className={item.icon}></i>
                    {item.label}
                    {item.count > 0 && (
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-background-100 text-foreground-500">{item.count}</span>
                    )}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}